/**
 * Button component overrides.
 * Modern gradient primary buttons with glow effects.
 */

import type { Theme } from '@mui/material/styles';
import themeConfig from '../../@configs/themeConfig';

const iconStyles = (size?: string) => ({
  '& > *:nth-of-type(1)': {
    ...(size === 'small' ? { fontSize: '14px' } : size === 'medium' ? { fontSize: '16px' } : { fontSize: '20px' }),
  },
});

const button: Theme['components'] = {
  MuiButtonBase: {
    defaultProps: {
      disableRipple: themeConfig.disableRipple,
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ theme, ownerState }) => ({
        fontWeight: 500,
        textTransform: 'none' as const,
        transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        '&.Mui-disabled': {
          opacity: 0.45,
        },
        '&:not(.Mui-disabled):active': {
          transform: 'scale(0.97)',
        },
        ...(ownerState.variant === 'text'
          ? {
              ...(ownerState.size === 'small' && { padding: theme.spacing(1.5, 2.5) }),
              ...(ownerState.size === 'medium' && { padding: theme.spacing(2, 3) }),
              ...(ownerState.size === 'large' && { padding: theme.spacing(2.75, 4) }),
            }
          : ownerState.variant === 'outlined'
            ? {
                ...(ownerState.size === 'small' && { padding: theme.spacing(1.25, 3.25) }),
                ...(ownerState.size === 'medium' && { padding: theme.spacing(2.25, 4.75) }),
                ...(ownerState.size === 'large' && { padding: theme.spacing(2.75, 6.25) }),
              }
            : {
                ...(ownerState.size === 'small' && { padding: theme.spacing(1.5, 3.5) }),
                ...(ownerState.size === 'medium' && { padding: theme.spacing(2.5, 5) }),
                ...(ownerState.size === 'large' && { padding: theme.spacing(3, 6.5) }),
              }),
        variants: [
          {
            props: { variant: 'contained', color: 'primary' },
            style: {
              background: 'var(--gradient-primary)',
              '&:not(.Mui-disabled)': {
                boxShadow: 'var(--mui-customShadows-primary-sm)',
              },
              '&:not(.Mui-disabled):hover': {
                boxShadow: 'var(--glow-primary)',
                background: 'var(--gradient-primary)',
                filter: 'brightness(1.1)',
              },
              '&.Mui-disabled': {
                background: 'var(--gradient-primary)',
                color: 'var(--mui-palette-primary-contrastText)',
              },
            },
          },
          {
            props: { variant: 'text', color: 'primary' },
            style: {
              '&:not(.Mui-disabled):hover': {
                backgroundColor: 'var(--mui-palette-primary-lighterOpacity)',
              },
              '&.Mui-disabled': {
                color: 'var(--mui-palette-primary-main)',
              },
            },
          },
          {
            props: { variant: 'outlined', color: 'primary' },
            style: {
              '&:not(.Mui-disabled):hover': {
                backgroundColor: 'var(--mui-palette-primary-lighterOpacity)',
              },
            },
          },
        ],
      }),
      sizeSmall: ({ theme }) => ({
        lineHeight: 1.38462,
        fontSize: theme.typography.body2.fontSize,
        borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
      }),
      sizeLarge: {
        fontSize: '1.0625rem',
        lineHeight: 1.529412,
        borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
      },
      startIcon: ({ theme, ownerState }) => ({
        ...(ownerState.size === 'small'
          ? { marginInlineEnd: theme.spacing(1.5) }
          : ownerState.size === 'medium'
            ? { marginInlineEnd: theme.spacing(2) }
            : { marginInlineEnd: theme.spacing(2.5) }),
        ...iconStyles(ownerState.size),
      }),
      endIcon: ({ theme, ownerState }) => ({
        ...(ownerState.size === 'small'
          ? { marginInlineStart: theme.spacing(1.5) }
          : ownerState.size === 'medium'
            ? { marginInlineStart: theme.spacing(2) }
            : { marginInlineStart: theme.spacing(2.5) }),
        ...iconStyles(ownerState.size),
      }),
    },
  },
};

export default button;
